import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Search, Loader2, CheckCircle2, Clock, Phone,
  Package, XCircle, Mail, Send, UserCheck
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const STATUS_STEPS = [
  { key: 'pending', label: 'Application Received', icon: Clock, description: 'We have received your application and it is in the queue.' },
  { key: 'contacted', label: 'Contacted', icon: Phone, description: 'Our team has reached out to discuss your musical goals.' },
  { key: 'package_proposed', label: 'Package Proposed', icon: Package, description: 'We\'ve designed a custom semester package for you.' },
  { key: 'package_sent', label: 'Package Sent', icon: Send, description: 'Your personalized semester package has been sent for review.' },
  { key: 'enrolled', label: 'Enrolled', icon: CheckCircle2, description: 'Welcome aboard! You\'re officially enrolled.' },
];

const getStepIndex = (status) => {
  if (status === 'declined') return -1;
  const idx = STATUS_STEPS.findIndex(s => s.key === status);
  return idx >= 0 ? idx : 0;
};

export const TrackApplication = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const handleTrack = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }

    setLoading(true);
    setNotFound(false);
    setResult(null);

    try {
      const response = await axios.get(`${API}/track`, { params: { email } });
      if (response.data && response.data.length > 0) {
        setResult(response.data[0]);
      } else {
        setNotFound(true);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        setNotFound(true);
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const currentStepIndex = result ? getStepIndex(result.status) : -1;
  const isDeclined = result?.status === 'declined';

  return (
    <div className="min-h-screen bg-[#050505]">
      <Header />

      <main className="pt-24 pb-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <p className="font-accent text-[#D4AF37] text-sm tracking-[0.2em] uppercase mb-4">
              Application Status
            </p>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-[#EDEDED] mb-4">
              Track Your Application
            </h1>
            <p className="text-[#A1A1AA] max-w-xl mx-auto">
              Enter the email address you used during your application to see where you are in the process.
            </p>
          </div>

          {/* Search Form */}
          <form onSubmit={handleTrack} className="glass-card rounded-2xl p-8 mb-8">
            <div className="space-y-4">
              <Label htmlFor="track-email" className="text-[#F5F5DC]">Email Address</Label>
              <div className="flex gap-3">
                <div className="relative flex-1">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#A1A1AA]" size={18} />
                  <Input
                    id="track-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 bg-black/50 border-white/10 text-[#EDEDED]"
                    placeholder="Enter the email you used to apply"
                    data-testid="track-email-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="btn-gold rounded-full px-8"
                  data-testid="track-submit"
                >
                  {loading ? (
                    <Loader2 className="animate-spin" size={18} />
                  ) : (
                    <>
                      <Search size={18} className="mr-2" />
                      Track
                    </>
                  )}
                </Button>
              </div>
            </div>
          </form>

          {/* Not Found */}
          {notFound && (
            <div className="glass-card rounded-2xl p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-[#2A2A2A] flex items-center justify-center mx-auto mb-4">
                <Search className="text-[#A1A1AA]" size={28} />
              </div>
              <h3 className="font-heading text-xl text-[#F5F5DC] mb-2">No Application Found</h3>
              <p className="text-[#A1A1AA] mb-6">
                We couldn't find an application with that email address.
                Make sure you're using the same email you applied with.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a href="/apply">
                  <Button className="btn-gold rounded-full px-6">Apply as New Student</Button>
                </a>
                <a href="/repackage">
                  <Button variant="outline" className="rounded-full px-6 border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black">
                    Current Student
                  </Button>
                </a>
              </div>
            </div>
          )}

          {/* Result - Status Timeline */}
          {result && (
            <div className="glass-card rounded-2xl p-8">
              {/* Student Info */}
              <div className="flex items-center gap-4 mb-8 pb-6 border-b border-white/10">
                <div className="w-14 h-14 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
                  <UserCheck className="text-[#D4AF37]" size={24} />
                </div>
                <div>
                  <h3 className="font-heading text-xl text-[#F5F5DC]">{result.full_name}</h3>
                  <p className="text-[#A1A1AA] text-sm">
                    Applied on {new Date(result.created_at).toLocaleDateString('en-US', {
                      year: 'numeric', month: 'long', day: 'numeric'
                    })}
                    {' '}&middot;{' '}
                    <span className={
                      result.student_type === 'new' ? 'text-emerald-400' :
                      result.student_type === 'returning' ? 'text-purple-400' :
                      'text-blue-400'
                    }>
                      {result.student_type === 'new' ? 'New Student' :
                       result.student_type === 'returning' ? 'Returning Student' :
                       'Current Student'}
                    </span>
                  </p>
                </div>
              </div>

              {/* Declined State */}
              {isDeclined ? (
                <div className="text-center py-6">
                  <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
                    <XCircle className="text-red-400" size={32} />
                  </div>
                  <h3 className="font-heading text-xl text-[#F5F5DC] mb-2">Application Declined</h3>
                  <p className="text-[#A1A1AA] max-w-md mx-auto">
                    Unfortunately, your application was not accepted at this time.
                    Please reach out to us if you have questions or would like to reapply.
                  </p>
                </div>
              ) : (
                <>
                  {/* Progress Timeline */}
                  <div className="space-y-0">
                    {STATUS_STEPS.map((step, index) => {
                      const isCompleted = index <= currentStepIndex;
                      const isCurrent = index === currentStepIndex;
                      const StepIcon = step.icon;

                      return (
                        <div key={step.key} className="flex gap-4">
                          {/* Timeline Line + Dot */}
                          <div className="flex flex-col items-center">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                              isCompleted
                                ? 'bg-[#D4AF37] text-black'
                                : 'border-2 border-[#2A2A2A] text-[#A1A1AA]'
                            } ${isCurrent ? 'ring-4 ring-[#D4AF37]/20' : ''}`}>
                              <StepIcon size={18} />
                            </div>
                            {index < STATUS_STEPS.length - 1 && (
                              <div className={`w-0.5 h-16 ${
                                index < currentStepIndex ? 'bg-[#D4AF37]' : 'bg-[#2A2A2A]'
                              }`} />
                            )}
                          </div>

                          {/* Content */}
                          <div className={`pb-8 ${isCurrent ? '' : 'opacity-60'}`}>
                            <h4 className={`font-heading text-lg ${
                              isCompleted ? 'text-[#F5F5DC]' : 'text-[#A1A1AA]'
                            }`}>
                              {step.label}
                              {isCurrent && (
                                <span className="ml-2 text-xs font-body bg-[#D4AF37]/20 text-[#D4AF37] px-2 py-0.5 rounded-full">
                                  Current
                                </span>
                              )}
                            </h4>
                            <p className="text-[#A1A1AA] text-sm mt-1">{step.description}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Selected Classes */}
                  {result.class_interests?.length > 0 && (
                    <div className="mt-6 pt-6 border-t border-white/10">
                      <p className="text-[#A1A1AA] text-sm mb-3">Classes you selected ({result.class_interests.length})</p>
                      <div className="flex flex-wrap gap-2">
                        {result.class_interests.map((ci, idx) => (
                          <span key={idx} className="px-3 py-1 rounded-full bg-[#D4AF37]/10 text-[#D4AF37] text-sm">
                            {ci.class_name}
                            {ci.prefer_recording && ' (Recording)'}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* View Package Link */}
                  {result.custom_package_id && (result.status === 'package_sent' || result.status === 'enrolled') && (
                    <div className="mt-6 pt-6 border-t border-white/10">
                      <a href={`/view-package/${result.custom_package_id}`}>
                        <Button className="btn-gold rounded-full px-6">
                          <Package size={16} className="mr-2" />
                          View Your Semester Package
                        </Button>
                      </a>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default TrackApplication;
